import { apiMerchantPublic } from "@/api"
import { parseScheduleSlots, type ScheduleSlots } from "@/lib/scheduling/schedule-slots"
import { record, text } from "@/lib/scheduling/wire"
import type { ServiceType } from "@/types"
import axios from "axios"

/** Error code the gateway answers with when the flag is off (§9.3). */
export const FUTURE_WINDOWS_DISABLED = "SCHEDULING_FUTURE_WINDOWS_DISABLED"

/** Address the grid depends on; DELIVERY only, the gateway ignores it otherwise. */
export type ScheduleSlotsAddress = {
  lat?: number | null
  lng?: number | null
  district?: string | null
  city?: string | null
  state?: string | null
}

export type ScheduleSlotsQuery = {
  merchantId: string
  serviceType: ServiceType
  /** Inclusive horizon bounds as calendar dates on the tenant's clock. */
  from: string
  to: string
  itemOfferIds: string[]
  optionIds?: string[]
  address?: ScheduleSlotsAddress | null
}

function appendRepeated(params: URLSearchParams, key: string, values: string[]): void {
  for (const value of values) {
    if (value) params.append(key, value)
  }
}

/** Query string of a slots request; stable order, so it doubles as a cache key. */
export function scheduleSlotsParams(query: ScheduleSlotsQuery): string {
  const params = new URLSearchParams()
  params.set("serviceType", query.serviceType)
  params.set("from", query.from)
  params.set("to", query.to)
  appendRepeated(params, "itemOfferId", query.itemOfferIds)
  appendRepeated(params, "optionId", query.optionIds ?? [])

  const address = query.address
  if (query.serviceType === "DELIVERY" && address) {
    if (address.lat != null) params.set("lat", String(address.lat))
    if (address.lng != null) params.set("lng", String(address.lng))
    if (address.district) params.set("district", address.district)
    if (address.city) params.set("city", address.city)
    if (address.state) params.set("state", address.state)
  }

  return params.toString()
}

function isFutureWindowsDisabled(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) return false
  const body = record(error.response.data)
  const code = text(body?.code) || text(body?.error) || text(record(body?.error)?.code)
  return code === FUTURE_WINDOWS_DISABLED
}

/**
 * Reads the offered grid for a bag, a channel and (on DELIVERY) an address.
 *
 * Returns null when the merchant does not run the full model — the gateway
 * answers `404 SCHEDULING_FUTURE_WINDOWS_DISABLED` and the storefront simply
 * stays on the standard mode. Every other failure is thrown for the caller to
 * degrade on.
 */
export async function fetchScheduleSlots(
  query: ScheduleSlotsQuery,
  signal?: AbortSignal,
): Promise<ScheduleSlots | null> {
  const path = `/public/merchants/${encodeURIComponent(query.merchantId)}/schedule-slots`

  try {
    const { data } = await apiMerchantPublic.get<unknown>(`${path}?${scheduleSlotsParams(query)}`, {
      signal,
    })
    return parseScheduleSlots(data)
  } catch (error) {
    if (isFutureWindowsDisabled(error)) return null
    throw error
  }
}
